import { tenantTimezoneAtom } from "@/atoms/tenant-clock"
import { PrizeChoiceList } from "@/components/marketing/prize-redeem/PrizeChoiceList"
import { PrizeArt } from "@/components/marketing/PrizeArt"
import { Button, Card, Price, Text } from "@/components/ui"
import { prizeOfferLabel } from "@/domain/marketing/prize-offer"
import { prizeOrigin, prizeValidity } from "@/domain/marketing/won-prize"
import type { CartPrize, CartPrizeState } from "@/hooks/use-cart-prizes"
import { usePrizeOffer, useRedeemPrize } from "@/hooks/use-redeem-prize"
import type { CouponNotice } from "@/lib/discount-evaluation/coupon-copy"
import { useAtomValue } from "jotai"
import { useState } from "react"

function noticeText(notice: CouponNotice): string {
  if (notice.kind === "dropped") return notice.note
  if (notice.kind === "invalid") return "Este prêmio não vale neste pedido."
  return notice.blockers.map((blocker) => blocker.text).join(". ")
}

function statusLine(state: CartPrizeState): string {
  switch (state.kind) {
    case "checking":
      return "Conferindo seu prêmio…"
    case "applied":
      return "Aplicado nesta sacola."
    case "delivery":
      return "O desconto entra na taxa de entrega ao finalizar o pedido."
    case "missing-item":
      return "Coloque o item do prêmio na sacola para ele valer."
    case "blocked":
      return noticeText(state.notice)
    case "unchecked":
      return "O desconto aparece ao finalizar o pedido."
  }
}

function MissingItemAction({ prize }: { prize: CartPrize }) {
  const offer = usePrizeOffer(prize.benefit)
  const redeem = useRedeemPrize()
  const [picking, setPicking] = useState(false)
  if (!offer || offer.kind === "use") return null
  if (offer.kind === "pick" && picking) {
    return <PrizeChoiceList tone="store" choices={offer.choices} onChoose={(choice) => redeem(prize.prize.code, { kind: "item", choice })} />
  }
  function act() {
    if (!offer) return
    if (offer.kind === "pick") setPicking(true)
    else if (offer.kind === "add") redeem(prize.prize.code, { kind: "item", choice: offer.choice })
    else redeem(prize.prize.code, { kind: "menu" })
  }
  return (
    <Button size="sm" variant="soft" onClick={act} className="self-start">
      {prizeOfferLabel(offer)}
    </Button>
  )
}

function CartPrizeRow({ prize }: { prize: CartPrize }) {
  const timeZone = useAtomValue(tenantTimezoneAtom)
  const { state } = prize
  const validity = prizeValidity(prize.prize.expiresAt, timeZone)
  const title = `${prizeOrigin(prize.prize.gameType)}: ${prize.prize.prizeLabel || "seu prêmio"}`
  return (
    <Card as="li" padding="md" className="flex gap-3">
      <PrizeArt artKey={prize.prize.prizeArtKey} label={prize.prize.prizeLabel} size={40} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline justify-between gap-3">
          <Text weight="semibold" tone="default">{title}</Text>
          {state.kind === "applied" ? (
            <Price cents={state.discountCents} sign="minus" tone="secondary" className="shrink-0" />
          ) : null}
        </div>
        <Text size="sm" tone="muted" aria-live="polite">
          {statusLine(state)}
        </Text>
        {validity ? <Text size="xs" tone="subtle">{validity}</Text> : null}
        {state.kind === "missing-item" ? <MissingItemAction prize={prize} /> : null}
      </div>
    </Card>
  )
}

/** The won prizes riding on this bag, each with what coupon-service takes off (or why not yet) and its validity; nothing when no prize is attached. */
export function CartPrizes({ prizes }: { prizes: readonly CartPrize[] }) {
  if (prizes.length === 0) return null
  return (
    <section aria-label="Prêmios no pedido" className="mb-3">
      <ul className="flex flex-col gap-3">
        {prizes.map((prize) => (
          <CartPrizeRow key={prize.prize.drawId} prize={prize} />
        ))}
      </ul>
    </section>
  )
}
